import * as XLSX from "xlsx";

const HEADER_ALIASES = {
  brand: ["brand"],
  style: ["style"],
  product: ["product"],
  color: ["color (jiffy)", "color", "colour"],
  size: ["size"],
  variant_id: ["jiffy variant id", "variant id", "variant"],
  gtin: ["our gtin", "gtin"],
  units_per_case: ["units per case"],
  units_received: ["units received"],
  units_ordered: ["units ordered", "quantity", "ordered", "qty"],
};

const normHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const findHeaderIndex = (rows) => {
  for (let i = 0; i < Math.min(rows.length, 12); i += 1) {
    const cells = (rows[i] || []).map(normHeader);
    if (cells.includes("brand") && cells.includes("style")) return i;
  }
  return -1;
};

const mapHeaders = (headerRow) => {
  const map = {};
  headerRow.forEach((cell, idx) => {
    const name = normHeader(cell);
    Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
      if (aliases.includes(name) && map[key] === undefined) {
        map[key] = idx;
      }
    });
  });
  return map;
};

const cell = (row, map, key) => String(row[map[key]] ?? "").trim();

const toQty = (row, map) => {
  const received = Number(cell(row, map, "units_received")) || 0;
  if (received > 0) return received;
  return Number(cell(row, map, "units_ordered")) || 0;
};

const sheetPoNumber = (sheetName, rows) => {
  for (let i = 0; i < Math.min(rows.length, 8); i += 1) {
    const row = (rows[i] || []).map((c) => String(c || "").trim());
    if (!row[0]) continue;
    if (/^po number/i.test(row[0])) {
      if (row[1]) return row[1];
      return row[0].replace(/po number:\s*/i, "").trim();
    }
  }
  return String(sheetName || "").trim();
};

export const parseBlankOrdersWorkbook = (arrayBuffer) => {
  const workbook = XLSX.read(arrayBuffer, { type: "array", raw: false });
  const items = [];

  workbook.SheetNames.forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
      header: 1,
      defval: "",
      raw: false,
    });
    const headerIdx = findHeaderIndex(rows);
    if (headerIdx < 0) return;

    const map = mapHeaders(rows[headerIdx] || []);
    const po = sheetPoNumber(sheetName, rows);

    for (let i = headerIdx + 1; i < rows.length; i += 1) {
      const row = rows[i] || [];
      const style = cell(row, map, "style");
      const color = cell(row, map, "color");
      const size = cell(row, map, "size");
      const variantId = cell(row, map, "variant_id");
      const qty = toQty(row, map);
      if ((!style && !variantId) || qty <= 0) continue;

      items.push({
        po,
        brand: cell(row, map, "brand"),
        style,
        product: cell(row, map, "product"),
        color,
        size,
        jiffy_variant_id: variantId,
        gtin: cell(row, map, "gtin"),
        units_per_case: Number(cell(row, map, "units_per_case")) || 0,
        qty,
      });
    }
  });

  return items;
};

export const mergeBlankOrderItems = (items) => {
  const merged = new Map();
  items.forEach((item) => {
    const key =
      item.jiffy_variant_id || `${item.style}|${item.color}|${item.size}`;
    if (!merged.has(key)) {
      merged.set(key, { ...item, pos: item.po ? [item.po] : [] });
      return;
    }
    const current = merged.get(key);
    current.qty += item.qty;
    if (item.po && !current.pos.includes(item.po)) current.pos.push(item.po);
  });
  return [...merged.values()];
};
